<template>
  <v-dialog
    v-model="dialog"
    width="max-content"
    persistent
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        v-on="on"
      >
        <v-icon>fa-user</v-icon>
      </v-btn>
    </template>
    <v-card-title>
      <v-row>
        <v-col cols="auto">
          <h3>Benutzereinstellungen</h3>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            icon
            @click="abortDialog"
          >
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-form
        ref="editForm"
        v-model="valid"
        class="user-settings-content-box"
      > 
        <div id="user-salutation">
          <v-select
            v-model="newUser.salutation"
            name="salutation"
            label="Anrede"
            :items="['Herr', 'Frau', 'Keine Angabe']"
            @change="evalUserFieldChange('salutation')"
          />
        </div>
        <div id="user-title">
          <v-text-field
            v-model="newUser.title"
            name="title"
            label="Titel"
            placeholder="Dr."
            @blur="evalUserFieldChange('title')"
          />
        </div>
        <div id="user-firstname">
          <v-text-field
            v-model="newUser.firstname"
            name="firstname"
            label="Vorname"
            placeholder="Max"
            @blur="evalUserFieldChange('firstname')"
          />
        </div>
        <div id="user-lastname">
          <v-text-field
            v-model="newUser.lastname"
            name="lastname"
            label="Nachname"
            placeholder="Mustermann"
            @blur="evalUserFieldChange('lastname')"
          />
        </div>
        <div id="user-email">
          <v-text-field
            v-model="newUser.email"
            name="email"
            label="E-Mail-Adresse"
            placeholder="Max.Mustermann@spicetech.de"
            :rules="emailRules"
            @blur="evalUserFieldChange('email')"
          />
        </div>
        <div id="user-password">
          <v-text-field
            v-model="newUser.password"
            name="password"
            label="Neues Passwort"
            placeholder="****"
            :rules="passwordRules"
            :append-icon="pwVisibility ? 'fa-eye-slash' : 'fa-eye'"
            :type="pwVisibility ? 'text' : 'password'"
            @click:append="pwVisibility = !pwVisibility"
            @change="evalUserFieldChange('password'); validateForm();"
          />
        </div>
        <div id="user-password-repeat">
          <v-text-field
            v-model="passwordRepeat"
            name="password-repeat"
            label="Passwort wiederholen"
            placeholder="****"
            :type="pwVisibility ? 'text' : 'password'"
            :rules="passwordRepeatRules"
          />
        </div>
      </v-form>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <v-container>
        <v-row>
          <v-col>
            <v-form
              ref="oldPassword"
              v-model="oldPasswordForm"
            >
              <v-text-field
                v-model="oldPassword"
                name="old-password"
                label="Aktuelles Passwort"
                placeholder="****"
                :rules="oldPasswordRules"
                :append-icon="pwVisibilityOld ? 'fa-eye-slash' : 'fa-eye'"
                :type="pwVisibilityOld ? 'text' : 'password'"
                required
                @click:append="pwVisibilityOld = !pwVisibilityOld"
              />
            </v-form>
          </v-col>
          <v-col class="d-flex align-center justify-center">
            <v-btn
              grow
              color="primary"
              :disabled="!(anyUserFieldChanged && valid && oldPasswordForm)"
              :loading="loading"
              @click="saveSettings"
            >
              Änderungen Speichern
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import * as R from 'ramda'

export default {
  name: "UserSettings",
  data: () => ({
    dialog: false,
    valid: false,
    passwordValid: false,
    userHasToChangeMail: false,
    newUser: {
      email: "",
      firstname: "",
      lastname: "",
      salutation: "",
      title: "",
      password: ""
    },
    userCopy: null,
    userFieldChange: {},
    anyUserFieldChanged: false,
    oldPassword: "",
    oldPasswordForm: false,
    passwordRepeat: "",
    passwordRules: [
      v =>
        v.length === 0 ||
        v.length > 4 ||
        "das Passwort muss mehr als 4 Zeichen enthalten"
    ],
    emailRules: [
      v =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
          "die E-Mail muss gültig sein",
      v => !!v || "E-Mail ist erforderlich" 
    ],
    oldPasswordRules: [v => !!v || "Zum Speichern der Änderungen bitte aktuelles Passwort eingeben"],
    pwVisibility: false,
    pwVisibilityOld: false,
    loading: false
  }),
  computed: {
    ...mapGetters(["user"]),
    passwordRepeatRules() {
      return [
        v => !this.newUser.password ||
             !!v || "Passwortwiederholung ist erforderlich",
        v =>
          v == this.newUser.password || "die Passwörter müssen übereinstimmen"
      ];
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.setupDialog();
      }
    }
  },
  mounted() {
    window.EventBus.$on("showUserSettingsDialog", () => {
      this.dialog = true;
    });
  },
  methods: {
    ...mapActions(["setUser", "showSnackbar"]),
    ...mapMutations({mutationSetUser: "setUser"}),
    setupDialog() {
      this.userFieldChange = {};
      this.anyUserFieldChanged = false;
      this.oldPassword = "";
      this.passwordRepeat = "";
      // map null values to empty string for form
      this.newUser = R.map((val) => val ? val : "", {...this.user, password: ""});
      if (!this.newUser.salutation) {
        // map null salutation to "Keine Angabe" for form
        this.newUser.salutation = "Keine Angabe"
      }
      this.$nextTick(() => this.$refs.oldPassword.validate());   
      this.$nextTick(() =>this.$refs.editForm.validate());
      this.userCopy = R.clone(this.newUser);
    },
    // check if field value has been changed
    evalUserFieldChange(field) {
      this.userFieldChange[field] = this.newUser[field] != this.userCopy[field];
      this.anyUserFieldChanged = R.any((val) => val == true)(R.values(this.userFieldChange))
    },
    validateForm() {
      this.$nextTick(() =>this.$refs.editForm.validate());
    },
    saveSettings() {
      this.loading = true;
      // convert title to null if necessary
      var title = this.newUser.title ? this.newUser.title : null;
      this.setUser({ 
        ...this.newUser,
        oldPassword: this.oldPassword,
        title: title
      }).then(res => {
        if (res.data.user) {
          this.mutationSetUser({ ...res.data.user });
          this.showSnackbar(
            {
              text: "Benutzerdaten erfolgreich aktualisiert",
              timeout: "3000",
              type: "success"
            }
          );
          this.setupDialog();
        } else {
          throw res.data.error;
        }
        this.loading = false;
      })
        .catch(error => {
          var errorText = "Ein Fehler ist aufgetreten."
          if (error.response.status == 403) {
            errorText =  "Falsches Passwort."
          } else {
            console.error(error);
          }
          this.showSnackbar(
            {
              text: `${errorText} Bitte versuchen Sie es erneut.`,
              timeout: "3000",
              type: "error"
            }
          )
          this.loading = false;
        });
    },
    abortDialog() {
      this.dialog = false;
      if (this.userHasToChangeMail) {
        this.userHasToChangeMail = false;
        window.EventBus.$emit("runShepherd");
      }
    }
  }
};
</script>

<style>
.v-dialog {
  background: #fff;
}

#user-email {
  grid-area: email;
}
#user-firstname {
  grid-area: firstname;
}
#user-lastname {
  grid-area: lastname;
}
#user-title {
  grid-area: title;
}
#user-salutation {
  grid-area: salutation;
}
#user-password {
  grid-area: password;
}
#user-password-repeat {
  grid-area: password-repeat;
}

.user-settings-content-box {
  display: grid;
  grid-template-columns: 300px 300px;
  grid-template-rows: auto;
  grid-template-areas:
    "salutation title"
    "firstname lastname"
    "email email"
    "password password-repeat";
  gap: 20px;
  justify-items: stretch;
  align-items: center;
}
</style>
